import { useState } from 'react';
import { IC_KULLANICI_SAPKALARI } from '@isg/validation';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import type { PtwConfigOverview, RoleType } from '@isg/shared-types';
import { usePtwConfig, usePtwConfigYazma } from '../api/queries';
import { SAPKA_ETIKET } from '../i18n/tr';
import { QueryStatus } from './parts';
import { AyarDefteri, KontrolMatrisiKarti } from './kontrol-matrisi-parts';
import { PTW_KONTROL_MATRISI_ANAHTARI } from '@isg/validation';
import { ANLAM } from '../theme';

/**
 * PTW YAPILANDIRMA YÜZEYİ (F3·S6 — S0-K3 "🔧 tek yapılandırma yüzeyi").
 *
 * TEK EKRAN, DÖRT ANAHTAR AİLESİ; üçü ayrı EKSENDE yaşar ve ekran bunu
 * görünür kılar (eksen karışırsa yönetici yanlış yerde arar):
 *   · **İşyeri × izin türü** — kişisel kilit katmanı (K5),
 *   · **Tenant × (tür → zorunlu görev)** — MÜŞTERİ matrisi (Kapsam §19/14),
 *   · **Tenant × şapka listeleri** — geri çekme / saha kontrolü (S0-K1/K3),
 *   · **QR özet kapsamı** — madde 54.
 *
 * TABAN KURALLARI SUNUCUDADIR, EKRAN YALNIZ YANSITIR (§2.16):
 *   · saha kontrol listesi BOŞ BIRAKILAMAZ → uç 422 verir; ekran uyarıyı
 *     gösterir ama kendi kuralını yazmaz (kaydet düğmesi kapatılmaz — hata
 *     sunucunun cümlesiyle gelir, iki kaynak doğmaz),
 *   · geri çekmede TABAN İZİN VEREN KODDADIR: listeden çıkarılamaz çünkü
 *     listede DEĞİLDİR. Ekran bunu bilgi satırı olarak söyler — "kimse geri
 *     çekemiyor" yanlış yapılandırması imkânsızdır.
 *   · **Kütüphane hiçbir türde görev DAYATMAZ**: matris boş doğar ve boş
 *     kalması geçerli bir karardır.
 */
export function PtwYapilandirmaScreen(): React.JSX.Element {
  const config = usePtwConfig();
  return (
    <Stack maw={980}>
      <Title order={3}>PTW yapılandırma</Title>
      <QueryStatus queries={[config]} />
      {config.isSuccess && (
        <>
          <TurGorevMatrisi config={config.data} />
          {/* ✏️ DİLİM 2 (K-73a…g): kontrol matrisi tür → zorunlu görev
              matrisinin ALTINDA; değişiklik defteri hemen ardından. */}
          <KontrolMatrisiKarti config={config.data} />
          <AyarDefteri
            anahtar={PTW_KONTROL_MATRISI_ANAHTARI}
            baslik="Kontrol matrisi — değişiklik defteri"
            testId="kontrol-matrisi-defteri"
          />
          <IsyeriTurAnahtarlari config={config.data} />
          <SapkaListeleri config={config.data} />
          <QrKapsami config={config.data} />
        </>
      )}
    </Stack>
  );
}

/** TÜR → ZORUNLU GÖREV (Kapsam §19/14 — müşteri yapılandırması). */
function TurGorevMatrisi({ config }: { config: PtwConfigOverview }): React.JSX.Element {
  const yaz = usePtwConfigYazma();
  const [turId, setTurId] = useState<string | null>(null);
  const seciliSatir = config.turGorevMatrisi.find((s) => s.izinTuruRefId === turId);
  const [gorevIds, setGorevIds] = useState<string[] | null>(null);
  const gecerliGorevler = gorevIds ?? seciliSatir?.gorevler.map((g) => g.refId) ?? [];

  return (
    <Card withBorder data-testid="ptw-config-tur-gorev">
      <Stack gap="xs">
        <Text fw={600}>Tür → zorunlu görev matrisi</Text>
        <Text size="sm" c="dimmed">
          Hangi izin türünde hangi görevin zorunlu olduğu SİZİN kararınızdır — kütüphane hiçbir
          türde görev dayatmaz. Boş bırakmak geçerlidir. Sistemin değişmez işi ayrıdır: seçilen
          katılımcının o görev için gerekli yetkinliği taşıyıp taşımadığı her hâlükârda denetlenir.
        </Text>
        <Table withTableBorder data-testid="ptw-config-matris-tablosu">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>İzin türü</Table.Th>
              <Table.Th>Zorunlu görevler</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {config.izinTurleri.map((tur) => {
              const satir = config.turGorevMatrisi.find((s) => s.izinTuruRefId === tur.id);
              return (
                <Table.Tr key={tur.id} data-testid="ptw-config-matris-satiri">
                  <Table.Td>{tur.ad}</Table.Td>
                  <Table.Td>
                    {satir && satir.gorevler.length > 0 ? (
                      <Group gap={4}>
                        {satir.gorevler.map((g) => (
                          <Badge key={g.refId} variant="light">
                            {g.ad}
                          </Badge>
                        ))}
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        <Group align="flex-end" gap="xs">
          <Select
            label="İzin türü"
            data={config.izinTurleri.map((t) => ({ value: t.id, label: t.ad }))}
            value={turId}
            onChange={(deger) => {
              setTurId(deger);
              setGorevIds(null);
            }}
            data-testid="ptw-config-tur-secimi"
            searchable
          />
          <MultiSelect
            label="Zorunlu görevler"
            data={config.gorevler.map((g) => ({ value: g.id, label: g.ad }))}
            value={gecerliGorevler}
            onChange={setGorevIds}
            disabled={turId === null}
            data-testid="ptw-config-gorev-secimi"
            searchable
            style={{ minWidth: 320 }}
          />
          <Button
            disabled={turId === null}
            loading={yaz.isPending}
            data-testid="ptw-config-matris-kaydet"
            onClick={() =>
              yaz.mutate(
                {
                  yol: 'type-gorev',
                  govde: { izinTuruRefId: turId, gorevRefIds: gecerliGorevler },
                },
                { onSuccess: () => setGorevIds(null) },
              )
            }
          >
            Kaydet
          </Button>
        </Group>
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/** İŞYERİ × TÜR (K5 — kişisel kilit katmanı; varsayılan KAPALI). */
function IsyeriTurAnahtarlari({ config }: { config: PtwConfigOverview }): React.JSX.Element {
  const yaz = usePtwConfigYazma();
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [turId, setTurId] = useState<string | null>(null);
  const mevcut = config.isyeriTurAyarlari.find(
    (a) => a.workplaceId === workplaceId && a.izinTuruRefId === turId,
  );

  return (
    <Card withBorder data-testid="ptw-config-isyeri-tur">
      <Stack gap="xs">
        <Text fw={600}>İşyeri × izin türü anahtarları</Text>
        <Text size="sm" c="dimmed">
          Kişisel kilit katmanı (K5): açıkken kişisel kilitler çıkmadan izolasyon kaldırılamaz,
          izolasyon kalkmadan kapanış onaylanamaz. Yazılmamış hücrede varsayılan geçerlidir — katman
          KAPALI.
        </Text>
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>İşyeri</Table.Th>
              <Table.Th>İzin türü</Table.Th>
              <Table.Th>Kişisel kilit</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {config.isyeriTurAyarlari.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text size="sm" c="dimmed">
                    Yazılmış anahtar yok — hepsi varsayılanda (kapalı).
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {config.isyeriTurAyarlari.map((ayar) => (
              <Table.Tr
                key={`${ayar.workplaceId}-${ayar.izinTuruRefId}`}
                data-testid="ptw-config-isyeri-satiri"
              >
                <Table.Td>{ayar.workplaceAd}</Table.Td>
                <Table.Td>{ayar.izinTuruAd}</Table.Td>
                <Table.Td>{ayar.kisiselKilitZorunlu ? 'Zorunlu' : 'Kapalı'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group align="flex-end" gap="xs">
          <Select
            label="İşyeri"
            data={config.isyerleri.map((w) => ({ value: w.id, label: w.ad }))}
            value={workplaceId}
            onChange={setWorkplaceId}
            data-testid="ptw-config-isyeri-secimi"
            searchable
          />
          <Select
            label="İzin türü"
            data={config.izinTurleri.map((t) => ({ value: t.id, label: t.ad }))}
            value={turId}
            onChange={setTurId}
            data-testid="ptw-config-isyeri-tur-secimi"
            searchable
          />
          <Switch
            label="Kişisel kilit zorunlu"
            checked={mevcut?.kisiselKilitZorunlu ?? false}
            disabled={workplaceId === null || turId === null || yaz.isPending}
            data-testid="ptw-config-kilit-anahtari"
            onChange={(olay) =>
              yaz.mutate({
                yol: 'workplace-type',
                govde: {
                  workplaceId,
                  izinTuruRefId: turId,
                  kisiselKilitZorunlu: olay.currentTarget.checked,
                },
              })
            }
          />
        </Group>
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/** ŞAPKA LİSTELERİ (S0-K1/K3). */
function SapkaListeleri({ config }: { config: PtwConfigOverview }): React.JSX.Element {
  const yaz = usePtwConfigYazma();
  const [geriCekme, setGeriCekme] = useState<string[] | null>(null);
  const [sahaKontrol, setSahaKontrol] = useState<string[] | null>(null);
  const sapkaSecenekleri = (Object.entries(SAPKA_ETIKET) as [RoleType, string][])
    // Alan sözlüğü: PTW şapka listeleri iç kullanıcılardan seçilir (P13-b — ekran süzgeç yazmaz).
    .filter(([rol]) => IC_KULLANICI_SAPKALARI.includes(rol))
    .map(([rol, etiket]) => ({ value: rol, label: etiket }));

  return (
    <Card withBorder data-testid="ptw-config-authority">
      <Stack gap="xs">
        <Text fw={600}>Yetki listeleri</Text>
        <Alert color={ANLAM.bilgi} data-testid="ptw-config-taban-notu">
          <Text size="sm">
            İzni VEREN kişi geri çekmeyi her zaman tetikleyebilir — bu taban yetki listede değildir
            ve çıkarılamaz. Aşağıdaki liste yalnız EK şapkaları tutar.
          </Text>
        </Alert>
        <MultiSelect
          label="Geri çekme yetkisi olan şapkalar"
          data={sapkaSecenekleri}
          value={geriCekme ?? config.geriCekmeSapkalari}
          onChange={setGeriCekme}
          data-testid="ptw-config-geri-cekme"
          searchable
        />
        <MultiSelect
          label="Saha kontrolü yapabilen şapkalar"
          description="Boş bırakılamaz: liste boşsa kapanışın saha kontrolünü kimse yapamaz."
          data={sapkaSecenekleri}
          value={sahaKontrol ?? config.sahaKontrolSapkalari}
          onChange={setSahaKontrol}
          data-testid="ptw-config-saha-kontrol"
          searchable
        />
        <Group>
          <Button
            variant="light"
            loading={yaz.isPending}
            data-testid="ptw-config-authority-kaydet"
            onClick={() => {
              yaz.mutate(
                {
                  yol: 'authority',
                  govde: { kind: 'geri_cekme', sapkalar: geriCekme ?? config.geriCekmeSapkalari },
                },
                { onSuccess: () => setGeriCekme(null) },
              );
              yaz.mutate(
                {
                  yol: 'authority',
                  govde: {
                    kind: 'saha_kontrol',
                    sapkalar: sahaKontrol ?? config.sahaKontrolSapkalari,
                  },
                },
                { onSuccess: () => setSahaKontrol(null) },
              );
            }}
          >
            Listeleri kaydet
          </Button>
        </Group>
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/** QR ÖZET KAPSAMI (madde 54) — holding genişliği YOKTUR. */
function QrKapsami({ config }: { config: PtwConfigOverview }): React.JSX.Element {
  const yaz = usePtwConfigYazma();
  return (
    <Card withBorder data-testid="ptw-config-qr">
      <Stack gap="xs">
        <Text fw={600}>İzin QR'ının görünürlüğü</Text>
        <Text size="sm" c="dimmed">
          Özet her hâlükârda DARDIR ve kimlik verisi taşımaz (izin türü · durum · geçerlilik ·
          lokasyon); okuma oturum ister. Kapsam holding geneline açılamaz.
        </Text>
        <Select
          label="Kapsam"
          data={[
            { value: 'sirket', label: 'İznin şirketindeki herkes (varsayılan)' },
            { value: 'lokasyon', label: 'Yalnız iznin lokasyonunu kapsayan yetkililer' },
          ]}
          value={config.qrOzetKapsami}
          allowDeselect={false}
          data-testid="ptw-config-qr-kapsam"
          onChange={(deger) => deger && yaz.mutate({ yol: 'qr-kapsam', govde: { kapsam: deger } })}
        />
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/** Sunucunun cümlesi — ekran ikinci mesaj sözlüğü tutmaz (K8/§2.16). */
function YazmaHatasi({ hata }: { hata: Error | null }): React.JSX.Element | null {
  if (!hata) return null;
  return (
    <Text size="sm" c={ANLAM.engel} role="alert" data-testid="ptw-config-hata">
      {hata.message}
    </Text>
  );
}
